import { useEffect } from 'react';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const logoUrl =
  'https://storage.readdy-site.link/project_files/178025fa-ed44-4d24-9298-29f9cea8fee2/53d6ad4c-a079-4800-8b26-3a1d908f55e4_7cd11bc1-1586-4014-936d-323643baf9ea.jpeg?v=782c57361b8aa8c517a17263487a48c5';

const webSiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${siteUrl}/#website`,
  url: siteUrl,
  name: 'SunRoute Group LLC',
  description:
    'Professional freight transportation and logistics services throughout the United States',
  inLanguage: ['en-US', 'es'],
  publisher: {
    '@id': `${siteUrl}/#organization`,
  },
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${siteUrl}/?search={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
};

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${siteUrl}/#organization`,
  name: 'SunRoute Group LLC',
  alternateName: 'SunRoute Group',
  url: siteUrl,
  logo: {
    '@type': 'ImageObject',
    url: logoUrl,
    width: 512,
    height: 512,
  },
  description:
    'SunRoute Group LLC is a professional freight transportation and logistics company providing reliable box truck delivery and cargo transport services throughout the United States. Licensed DOT carrier with 24/7 customer support.',
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'US',
  },
  contactPoint: [
    {
      '@type': 'ContactPoint',
      telephone: '+1-727-203-6714',
      contactType: 'Customer Service',
      availableLanguage: ['English', 'Spanish'],
      contactOption: 'TollFree',
      areaServed: 'US',
    },
  ],
  sameAs: ['https://www.facebook.com/share/1B1H6GYJJa/?mibextid=wwXIfr'],
  areaServed: {
    '@type': 'Country',
    name: 'United States',
  },
  serviceType: [
    'Freight Transportation',
    'Box Truck Delivery',
    'Logistics Services',
    'Cargo Transport',
  ],
};

const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': `${siteUrl}/#localbusiness`,
  name: 'SunRoute Group LLC',
  image: logoUrl,
  url: siteUrl,
  telephone: '+1-727-203-6714',
  description:
    'Professional freight transportation and logistics services throughout the United States. Box truck delivery, cargo transport, and shipping solutions available 24/7.',
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'US',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 37.0902,
    longitude: -95.7129,
  },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday',
      ],
      opens: '00:00',
      closes: '23:59',
    },
  ],
  priceRange: '$$',
  areaServed: {
    '@type': 'Country',
    name: 'United States',
  },
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Transportation Services',
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Box Truck Delivery',
          description:
            'Reliable box truck delivery services throughout the United States',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Freight Transportation',
          description:
            'Professional freight transportation services throughout the United States',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Cargo Transport',
          description:
            'Safe and timely cargo transport solutions throughout the United States',
        },
      },
    ],
  },
};

const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${siteUrl}/#webpage`,
  url: siteUrl,
  name: 'SunRoute Group LLC - Professional Freight Transportation & Logistics Services throughout the United States',
  description:
    'SunRoute Group LLC offers professional freight transportation, box truck delivery, and logistics services throughout the United States. Licensed DOT carrier available 24/7.',
  dateModified: '2026-04-01',
  datePublished: '2025-01-01',
  isPartOf: {
    '@type': 'WebSite',
    '@id': `${siteUrl}/#website`,
    url: siteUrl,
    name: 'SunRoute Group',
  },
  about: {
    '@id': `${siteUrl}/#localbusiness`,
  },
  inLanguage: ['en-US', 'es'],
  breadcrumb: {
    '@id': `${siteUrl}/#breadcrumb`,
  },
  primaryImageOfPage: {
    '@type': 'ImageObject',
    url: logoUrl,
  },
  speakable: {
    '@type': 'SpeakableSpecification',
    cssSelector: ['h1', 'h2', '.hero-title'],
  },
};

const transportationServiceSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  '@id': `${siteUrl}/#service`,
  name: 'Freight Transportation & Logistics',
  serviceType: 'Freight Transportation',
  provider: {
    '@id': `${siteUrl}/#organization`,
  },
  description:
    'SunRoute Group LLC provides reliable and efficient freight transportation services throughout the United States. Our box trucks are fully equipped to handle commercial deliveries, scheduled pickups, and time-sensitive shipments with punctuality and professional care.',
  areaServed: {
    '@type': 'Country',
    name: 'United States',
  },
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Freight & Logistics Services',
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Dry and General Freight Transportation',
          description: 'Local and regional deliveries with responsible cargo handling',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Logistics Coordination',
          description:
            'Load coordination, shipment tracking, and operational management',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Scheduled Pickups & Commercial Deliveries',
          description:
            'Time-sensitive shipments handled with punctuality and professional care',
        },
      },
    ],
  },
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  '@id': `${siteUrl}/#breadcrumb`,
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: siteUrl,
    },
  ],
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What areas does SunRoute Group serve?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'SunRoute Group LLC provides freight transportation and box truck delivery services throughout the United States with a strategically positioned operational network guaranteeing fast response times in every region.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is SunRoute Group available 24/7?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes, SunRoute Group offers 24/7 customer support and transportation services every day of the week, available 24 hours a day, 7 days a week.',
      },
    },
    {
      '@type': 'Question',
      name: 'What services does SunRoute Group offer?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'SunRoute Group LLC offers freight transportation, box truck delivery, cargo transport, logistics coordination, scheduled pickups, and time-sensitive shipment services throughout the United States.',
      },
    },
    {
      '@type': 'Question',
      name: 'How can I contact SunRoute Group?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'You can reach SunRoute Group by phone at +1-727-203-6714, via SMS, or through WhatsApp. We are available 24/7.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is SunRoute Group a licensed carrier?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes, SunRoute Group LLC is a licensed DOT carrier that complies with all federal and state regulations, operating with the highest safety and compliance standards.',
      },
    },
  ],
};

export default function SeoJsonLd() {
  useEffect(() => {
    const schemas = [
      webSiteSchema,
      organizationSchema,
      localBusinessSchema,
      webPageSchema,
      transportationServiceSchema,
      breadcrumbSchema,
      faqSchema,
    ];

    const scriptTags: HTMLScriptElement[] = [];

    schemas.forEach((schema, index) => {
      const existingScript = document.getElementById(`schema-jsonld-${index}`);
      if (existingScript) {
        existingScript.remove();
      }
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = `schema-jsonld-${index}`;
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
      scriptTags.push(script);
    });

    return () => {
      scriptTags.forEach((script) => {
        if (document.head.contains(script)) {
          document.head.removeChild(script);
        }
      });
    };
  }, []);

  return null;
}